import { useToast } from "@west-tennessee-consulting/ui";
import { useUpdateMspDocument } from "./msp-docs-graphql";

/**
 * Get shared users for a document
 * Returns the shared_with_users array from the document
 */
export function useMspDocumentShares(documentId: string, enabled = true) {
  // This hook is kept for backward compatibility but now just returns
  // the shared users from the document data itself
  // The modal should read from document.shared_with_users directly
  return {
    data: [] as string[],
    isLoading: false,
    refetch: () => Promise.resolve(),
  };
}

/**
 * Share a document with a user by adding them to shared_with_users array
 */
export function useShareMspDocument() {
  const { toast } = useToast();
  const updateDocument = useUpdateMspDocument();

  const mutateAsync = async ({
    documentId,
    userId,
    currentSharedUsers,
  }: {
    documentId: string;
    userId: string;
    currentSharedUsers?: string[] | null;
  }) => {
    try {
      // Get current shared users or use provided array
      const currentShares = currentSharedUsers || [];

      // Check if user is already shared
      if (currentShares.includes(userId)) {
        toast({
          title: "Already Shared",
          description: "This document is already shared with this user.",
        });
        return;
      }

      // Add user to shared_with_users array
      const updatedShares = [...currentShares, userId];

      await updateDocument.mutateAsync({
        id: documentId,
        input: {
          shared_with_users: updatedShares,
        },
      });

      // Note: refetchQueries is already handled by useUpdateMspDocument
      // which includes ["GetMspDocumentss", "GetMspDocuments"] in its mutation

      toast({
        title: "Document Shared",
        description: "Document has been shared successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to share document.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    mutateAsync,
    isPending: updateDocument.isPending,
  };
}

/**
 * Unshare a document with a user by removing them from shared_with_users array
 */
export function useUnshareMspDocument() {
  const { toast } = useToast();
  const updateDocument = useUpdateMspDocument();

  const mutateAsync = async ({
    documentId,
    userId,
    currentSharedUsers,
  }: {
    documentId: string;
    userId: string;
    currentSharedUsers?: string[] | null;
  }) => {
    try {
      // Get current shared users or use provided array
      const currentShares = currentSharedUsers || [];

      // Remove user from shared_with_users array
      const updatedShares = currentShares.filter((id) => id !== userId);

      await updateDocument.mutateAsync({
        id: documentId,
        input: {
          shared_with_users: updatedShares,
        },
      });

      // Note: refetchQueries is already handled by useUpdateMspDocument
      // which includes ["GetMspDocumentss", "GetMspDocuments"] in its mutation

      toast({
        title: "Access Removed",
        description: "User access has been removed successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove access.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    mutateAsync,
    isPending: updateDocument.isPending,
  };
}
